import type {
  EdgeConfig,
  EdgeConfigModel,
  EdgeConfigWidget,
  EdgeImage,
  EdgeRawConfig,
  EdgeRawConfigChild,
  EdgeRawConfigModel,
  EdgeRawConfigWidget,
  EdgeRawImage,
} from '@/edge/types';

/**
 * This is not read UUID\GUID
 * We using this to add uuid field to repeaters
 * to use them as unique id for the session time
 * and for key property for React
 *
 * We do not use genuine uuidv4 just because we don't need it
 * In case we will need it https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
 */

const s4 = () => {
  return Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .substring(1);
};

export const generateSimpleUuid = () => {
  return `${s4()}-${s4()}`;
};

export const keyByFromArray = (array: Record<string, any>[], key: string) =>
  (array || []).reduce((memo, item) => ({ ...memo, [key ? item[key] : item]: item }), {});

const convertOptionValueToMappedBoolean = (option: string) => {
  const BOOLEAN_OPTIONS = {
    true: true,
    false: false,
    enabled: true,
    disabled: false,
    visible: true,
    hidden: false,
    submenu: true,
    '': false,
  } as const;

  return Object.prototype.hasOwnProperty.call(BOOLEAN_OPTIONS, option)
    ? BOOLEAN_OPTIONS[option as keyof typeof BOOLEAN_OPTIONS]
    : option;
};

const getWidgets = (config: EdgeRawConfigChild): EdgeRawConfigWidget[] => {
  if (!(config && config.widgets)) {
    return [];
  }

  const children = config.children && config.children[0] ? getWidgets(config.children[0]) : [];
  return [...config.widgets, ...children];
};

const normalizeModel = (model: EdgeRawConfigModel): EdgeConfigModel => {
  return {
    strings: model.strings || {},
    images: model.images ? normalizeImages(model.images) : {},
    links: model.links || {},
    options: model.template_options ? normalizeOptions(model.template_options) : {},
    repeaters: model.repeaters ? normalizeRepeaters(model.repeaters) : {},
  };
};

const normalizeWidget = (widget: EdgeRawConfigWidget): EdgeConfigWidget => {
  return {
    type: widget.type,
    model: normalizeModel(widget.model),
  };
};

const normalizeOptions = (options: Record<string, string>) => {
  return Object.entries(options ?? {}).reduce((memo, [optionKey, optionValue]) => {
    return {
      ...memo,
      [optionKey]: convertOptionValueToMappedBoolean(optionValue),
    };
  }, {});
};

const normalizeRepeaters = (repeaters: Record<string, EdgeRawConfigModel[]>): Record<string, EdgeConfigModel[]> => {
  return Object.entries(repeaters ?? {}).reduce((memo, [repeaterName, edgeRawConfigModelList]) => {
    return {
      ...memo,
      [repeaterName]: edgeRawConfigModelList
        .map((edgeRawConfigModel) => normalizeModel(edgeRawConfigModel))
        .map((edgeConfigModel) => {
          return {
            ...edgeConfigModel,
            uuid: generateSimpleUuid(),
          };
        }),
    };
  }, {});
};

const normalizeImages = (images: Record<string, EdgeRawImage>): Record<string, EdgeImage> => {
  return Object.entries(images ?? {}).reduce((memo, [imageName, image]) => {
    return {
      ...memo,
      [imageName]: {
        src: image.href ?? '',
      },
    };
  }, {});
};

const getScenes = (config: EdgeRawConfigChild): string[] => {
  if (!(config && config.type)) {
    return [];
  }

  const children = config.children && config.children[0] ? getScenes(config.children[0]) : [];
  return [config.type, ...children];
};

/**
 * We are normalizing the config by providing default values for missing fields.
 * This is done to avoid having to check for the existence of fields in the code.
 * We are also changing image fields to be more convenient to use. [href] -> [src]
 * @param config The config will be received in response to the config request
 */
export const normalizeConfig = (config: EdgeRawConfig): EdgeConfig => {
  const widgets = config?.children?.[0] ? getWidgets(config.children[0]) : [];
  const normalizedWidgets = widgets.map(normalizeWidget);
  const widgetsByType = keyByFromArray(normalizedWidgets, 'type');

  return {
    // New props (normalized)
    scenes: config?.children?.[0] ? getScenes(config.children[0]) : [],
    widgets: widgetsByType,

    // Map as it is
    globals: config.globals || {},
    layout: config.layout || {},
    themeName: config.themeName || '',
    serverConfigurationItems: config.serverConfigurationItems || {},
    title: config.title || '',
    metaTags: config.metaTags || [],
    linkTags: config.linkTags || [],
    baseTag: config.baseTag || {},
    htmlAttributes: config.htmlAttributes || {},
  };
};
